import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { JobApiService } from '@fleet/api/job/job-api.service';
import {
  ApiResponse,
  IssueModel,
  JobLifecycleListenerModel,
  JobModel,
  JobProgressModel,
  JobSearchResultModel,
  JobStatusUpdateAndProgress,
} from '@fleet/model';
import { WebsocketService } from '@fleet/socket';
import {
  OnScreenNotification,
  OnscreenNotificationService,
} from '@fleet/ui/onscreen-notification/onscreen-notification.service';
import { failureNotification, jobColorFromStatus } from '@fleet/utilities';
import { BehaviorSubject, Observable } from 'rxjs';
import { auditTime, filter, map, shareReplay } from 'rxjs/operators';

import { AuthService } from '@fleet/auth/services/auth.service';

@Injectable({ providedIn: 'root' })
export class JobListenerService extends WebsocketService {
  jobListener: BehaviorSubject<JobLifecycleListenerModel> = new BehaviorSubject(
    null,
  );
  jobListenerRequest: BehaviorSubject<JobLifecycleListenerModel> =
    new BehaviorSubject(null);

  loading: BehaviorSubject<boolean> = new BehaviorSubject(false);
  issues: BehaviorSubject<IssueModel[]> = new BehaviorSubject([]);

  // Configurable batch interval for performance optimization (ms)
  private batchIntervalMs = 15000; // Default 15 seconds

  client = 'Default Holder';

  constructor(
    private jobApiService: JobApiService,
    authService: AuthService,
    private onScreenNotificationService: OnscreenNotificationService,
    httpClient: HttpClient,
    @Inject('env') env: any,
  ) {
    super(env, httpClient);
    this.messages$.subscribe({
      next: (message: any) => {
        if (message.type === 'JobLifecycleListenerModel') {
          this.setListener(message.data);
        }
      },
    });
  }

  get listener() {
    return this.jobListener.value;
  }

  get listener$() {
    return this.jobListener.asObservable();
  }

  get loading$() {
    return this.loading.asObservable();
  }

  get issues$() {
    return this.issues.asObservable();
  }

  get listenerParams$() {
    return this.listener$.pipe(
      filter((l) => l !== null),
      map((listener: JobLifecycleListenerModel) => {
        return {
          fromOffset: listener.fromOffset,
          toOffset: listener.toOffset,
          statuses: listener.statuses,
        } as JobLifecycleListenerModel;
      }),
    );
  }

  get currentJob$() {
    return this.jobListener.asObservable().pipe(
      map((jobLifecycle: JobLifecycleListenerModel) => {
        if (
          jobLifecycle &&
          jobLifecycle.statusUpdates &&
          jobLifecycle.statusUpdates.length === 1 &&
          jobLifecycle.jobId
        ) {
          return this.jobFromJobSearchResult(jobLifecycle.statusUpdates[0]);
        }
        return null;
      }),
    );
  }

  get currentJobProgressUpdate$() {
    return this.jobListener.asObservable().pipe(
      filter((jobLifecycle: JobLifecycleListenerModel) => {
        if (
          jobLifecycle &&
          jobLifecycle.progressUpdates &&
          jobLifecycle.progressUpdates.length === 1 &&
          jobLifecycle.jobId
        ) {
          return true;
        }
        return false;
      }),
      map(
        (jobLifecycle: JobLifecycleListenerModel) =>
          jobLifecycle.progressUpdates[0],
      ),
    );
  }

  jobFromJobSearchResult(jsr: JobSearchResultModel) {
    return {
      jobStatus: jsr.status,
      paymentStatus: jsr.paymentStatus,
      jobId: jsr.jobId,
    } as JobModel;
  }

  get jobs$(): Observable<JobStatusUpdateAndProgress[]> {
    return this.jobListener.asObservable().pipe(
      map((listener: JobLifecycleListenerModel) =>
        listener && listener.statusUpdates
          ? listener.statusUpdates.map((statusUpdate: JobSearchResultModel) => {
              const foundProgress = listener.progressUpdates
                ? listener.progressUpdates.find(
                    (s) => s.jobId === statusUpdate.jobId,
                  )
                : null;
              return {
                latestStatusUpdate: statusUpdate,
                latestJobProgress: foundProgress,
                statusColor: jobColorFromStatus(statusUpdate.status),
              } as JobStatusUpdateAndProgress;
            })
          : [],
      ),
    );
  }

  /**
   * Batched version of jobs$ - emits at most once per configured interval
   * Use for performance-conscious consumers that don't need immediate updates
   * Default: 15 seconds
   */
  get jobsBatched$(): Observable<JobStatusUpdateAndProgress[]> {
    return this.jobs$.pipe(
      auditTime(this.batchIntervalMs),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * Configure the batch interval for jobsBatched$ observable
   * @param ms Batch interval in milliseconds
   */
  setBatchInterval(ms: number): void {
    this.batchIntervalMs = ms;
  }

  setListener(jobListener: JobLifecycleListenerModel) {
    if (jobListener) {
      if (jobListener.delta === true) {
        //single update

        if (jobListener.eventType === 'BOOKING_EXPIRY_CONFIRMED') {
          if (
            jobListener.statusUpdates[0] &&
            jobListener.statusUpdates[0].status === 'EXPIRED'
          ) {
            //remove the job from the listener
            this.removeJobFromListener(jobListener.statusUpdates[0].jobId);
          }
        } else {
          const statusUpdate = jobListener.statusUpdates?.[0];
          if (statusUpdate) {
            let found = false;
            const statusUpdates =
              this.jobListener.value && this.jobListener.value.statusUpdates
                ? this.jobListener.value.statusUpdates.map(
                    (job: JobSearchResultModel) => {
                      if (job.jobId === statusUpdate.jobId) {
                        found = true;
                        return statusUpdate;
                      }
                      return job;
                    },
                  )
                : [];
            if (!found) {
              statusUpdates.push(statusUpdate);
            }
            this.jobListener.next({
              ...this.jobListener.value,
              statusUpdates: statusUpdates,
            });
          }
          const progressUpdate = jobListener.progressUpdates?.[0];
          if (progressUpdate) {
            let found = false;
            const progressUpdates =
              this.jobListener.value && this.jobListener.value.progressUpdates
                ? this.jobListener.value.progressUpdates.map(
                    (progress: JobProgressModel) => {
                      if (progress.jobId === progressUpdate.jobId) {
                        found = true;
                        return progressUpdate;
                      }
                      return progress;
                    },
                  )
                : [];
            if (!found) {
              progressUpdates.push(progressUpdate);
            }

            this.jobListener.next({
              ...this.jobListener.value,
              progressUpdates: progressUpdates,
            });
          }
        }

        // Filter out cancelled jobs with timeout
        // if (jobListener.statusUpdates) {
        //   jobListener.statusUpdates.forEach((statusUpdate) => {
        //     if (statusUpdate.status === 'CANCELLED') {
        //       this.removeJobFromListener(statusUpdate.jobId, 10000); // 10 second timeout
        //     }
        //   });
        // }
      } else if (jobListener.delta === false) {
        this.jobListener.next(jobListener);
      } else {
        //CHECK WITH SIMON - is this just a listener param update

        this.jobListener.next(jobListener);
      }
    } else {
      this.jobListener.next(jobListener);
    }
  }

  registerListener(jobListener: JobLifecycleListenerModel) {
    this.jobListenerRequest.next(jobListener);
    this.loading.next(true);
    this.issues.next([]);
    this.jobApiService.registerListener(jobListener).subscribe({
      next: (resp: ApiResponse<JobLifecycleListenerModel>) => {
        this.loading.next(false);

        //PUT MOCK HERE FOR DRIVER.
        this.jobListener.next(resp.data);
        //this.jobListener.next(CURRENT_MOCK_LISTENER_MODEL.data as any);

        this.connect(
          'jobLifecycleListener.' + this.jobListener.value.listenerId,
        );
      },
      error: (issues: IssueModel[]) => {
        this.loading.next(false);
        this.issues.next(issues);
      },
    });
  }

  updateListener(jobListener: JobLifecycleListenerModel) {
    this.jobListenerRequest.next(jobListener);
    this.loading.next(true);
    this.issues.next([]);
    this.jobApiService.updateListener(jobListener).subscribe({
      next: (resp: ApiResponse<JobLifecycleListenerModel>) => {
        this.loading.next(false);
        this.setListener(resp.data);
      },
      error: (issues: IssueModel[]) => {
        this.loading.next(false);
        this.issues.next(issues);
      },
    });
  }

  deregisterListener() {
    if (this.jobListener.value && this.jobListener.value.listenerId) {
      const listenerId = this.jobListener.value.listenerId;
      console.log('[RT] Deregistering Listener', {
        client: this.client,
        listenerId,
      });
      this.jobListener.next(null);
      this.jobListenerRequest.next(null);
      this.close();
      this.jobApiService.deregisterListener(listenerId).subscribe({
        error: () => undefined,
      });
    }
  }

  get isListening$() {
    return this.isOpen$;
  }

  configureListener(jobListener: JobLifecycleListenerModel) {
    if (!this.jobListener.value) {
      this.registerListener(jobListener);
    } else {
      this.updateListener(jobListener);
    }
  }

  dismissJob(jobId: string) {
    this.jobApiService
      .dismissJobFromListener(this.jobListener.value.listenerId, jobId)
      .subscribe({
        next: (resp: ApiResponse<JobLifecycleListenerModel>) => {
          this.removeJobFromListener(jobId);
        },
        error: (issues: IssueModel[]) => {
          this.onScreenNotificationService.setNotification({
            ...failureNotification,
            title: 'Failed to dismiss job!',
          } as OnScreenNotification);
        },
      });
  }

  removeJobFromListener(jobId: string, timeout?: number) {
    if (timeout) {
      setTimeout(() => {
        this.removeJobFromListener(jobId);
      }, timeout);
    } else {
      if (this.jobListener?.value?.statusUpdates) {
        this.jobListener.next({
          ...this.jobListener.value,
          statusUpdates: this.jobListener.value.statusUpdates.filter(
            (s) => s.jobId !== jobId,
          ),
        });
      }
    }
  }
}
